import styled from '@emotion/styled';
import { css } from '@emotion/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { H2, IframeWrap } from '../../../ui';
import { PageLayout } from '../../shared/layout/page/PageLayout';
import { useSearch } from '../domain/hooks/useSearch';
import { PageNavigation } from './components/PageNavigation';
import { SearchBar } from './components/SearchBar';
import { usePagination } from '../domain/hooks/usePagination';
import { IBlogsPageData } from '../domain/models/IBlogsPageData';
import { IBlogItem } from '../../shared/api/contentful/types/IBlogItem';
import { BlogsList } from './components/blogs/BlogsList';
import { IFRAME_SECTION_TYPE_ID } from '../../shared/api/contentful/constants';
import { useTranslation } from '../../shared/i18n/translate/useTranslation';
import { Sections } from '../../content-sections/ui';
import { IIframeSectionEntry } from '../../shared/api/contentful/types';
import { StyledPageHeader } from './NewsStoriesPage';
import {
	YearFilterValue,
	useAllYearsFilters,
} from '../domain/hooks/useAllYearsFilters';
import ContentWrap from '../../../ui/components/Layout/ContentWrap';
import Responsive from '../../../ui/utils/Responsive';
import { getSessionStorageItem } from '../../shared/utils/sessionStorage';
import { useNavigate } from '../../shared/router/useNavigate';
import { useLocale } from '../../shared/i18n/locale/useLocale';
import { getLocalizedPath } from '../../app/pages/ui/utils/getLocalizedPath';
import { ALL_CATEGORIES } from '../../shared/domain/utils/getContentCategories';
import { VisuallyHidden } from '../../../ui/components/VisuallyHidden/VisuallyHidden';

export const BlogsPage = ({
	page,
	blogs,
	pageNumber,
	itemsPerPage,
	categoriesFilter,
}: IBlogsPageData) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const locale = useLocale();

	const yearsFilters: YearFilterValue[] = useAllYearsFilters(blogs);

	const categoriesFiltersWithAllCategories = useMemo(
		() => [
			{ id: ALL_CATEGORIES, title: t('news_searchbar_all_categories') },
			...categoriesFilter,
		],
		[categoriesFilter],
	);

	// Use default values for initial render to avoid hydration mismatch
	const defaultYear = yearsFilters[0];
	const defaultCategory = categoriesFiltersWithAllCategories[0];

	const { searchParams, setSearchParams, filterList } = useSearch<IBlogItem>(
		defaultYear,
		defaultCategory,
	);

	// Track whether we've initialized from sessionStorage
	const [hasInitialized, setHasInitialized] = useState(false);

	// Read stored filters from sessionStorage after hydration
	useEffect(() => {
		if (hasInitialized) return;

		const storedYear = getSessionStorageItem('news-blogs-year-filter');
		const storedCategoryId = getSessionStorageItem(
			'news-blogs-category-filter',
		);

		const sessionYear = storedYear || defaultYear;
		const sessionCategory =
			categoriesFiltersWithAllCategories.find(
				(category) => category.id === storedCategoryId,
			) || defaultCategory;

		if (storedYear || storedCategoryId) {
			setSearchParams({
				year: sessionYear,
				category: sessionCategory,
				query: '',
			});
		}

		setHasInitialized(true);
	}, [
		hasInitialized,
		categoriesFiltersWithAllCategories,
		defaultYear,
		defaultCategory,
		setSearchParams,
	]);

	const filteredBlogs = useMemo(() => filterList(blogs), [searchParams, blogs]);

	const { currentPageItems, numberOfPages } = usePagination({
		list: filteredBlogs,
		pageNumber,
		itemsPerPage,
	});

	const blogsListRef = useRef<HTMLElement>(null);
	const isPaginatingRef = useRef(false);

	useEffect(() => {
		if (isPaginatingRef.current) {
			isPaginatingRef.current = false;
			blogsListRef.current?.focus();
		}
	}, [pageNumber]);

	const iframeSections = page.fields.sections?.filter(
		(section) => section.contentType === IFRAME_SECTION_TYPE_ID,
	) as IIframeSectionEntry[] | undefined;

	const otherSections = page.fields.sections?.filter(
		(section) => section.contentType !== IFRAME_SECTION_TYPE_ID,
	);

	return (
		<PageLayout showAlertsDialog page={page}>
			<StyledPageHeader page={page} />
			<SearchBar
				years={yearsFilters}
				categories={categoriesFiltersWithAllCategories}
				onSearch={async (value) => {
					await navigate(getLocalizedPath(`/news/blogs/1`, locale));

					setSearchParams(value);
				}}
			/>
			<VisuallyHidden as="div" aria-live="polite" role="status">
				{t('poi_filter_results_count', {
					total: filteredBlogs.length,
				})}
			</VisuallyHidden>
			<StyledContentWrap iframeSections={iframeSections}>
				<section
					ref={blogsListRef}
					tabIndex={-1}
					aria-label={t('pagination_label', { pageNumber, numberOfPages })}
				>
					<BlogsList blogs={currentPageItems} />
				</section>

				{filteredBlogs.length > 0 && (
					<PageNavigation
						pageNumber={pageNumber}
						numberOfPages={numberOfPages}
						onPageChange={() => {
							isPaginatingRef.current = true;
						}}
					/>
				)}

				{iframeSections && iframeSections.length > 0 && (
					<>
						<H2>{t('newsletter_sign_ups_title')}</H2>
						<Sections sections={iframeSections} />
					</>
				)}

				{otherSections && otherSections.length > 0 && (
					<Sections sections={otherSections} />
				)}
			</StyledContentWrap>
		</PageLayout>
	);
};

const StyledContentWrap = styled(ContentWrap)<{
	iframeSections?: IIframeSectionEntry[] | undefined;
}>`
	display: flex;
	flex-direction: column;
	gap: 32px;

	${({ iframeSections }) =>
		iframeSections &&
		css`
			section {
				padding-top: 0;
				padding-bottom: 0;
				margin: 0;

				${IframeWrap} {
					padding-bottom: 100px;

					@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
						padding-bottom: 80px;
					}
				}

				iframe {
					background: none;
					max-width: 500px;
				}
			}
		`}
`;
